/**
 * Screen → ground lat/lng for `gmp-map-3d` via camera ray / ground-plane intersection.
 * Linear bounds mapping fails at the viewport edges (perspective); this matches
 * range + tilt + heading + vertical FOV from the Maps 3D camera model.
 */

import {
  readMapCenter,
  readMapFov,
  readMapHeading,
  readMapRange,
  readMapTilt,
  type Map3dElement,
} from "./worldSoldPixels";

const DEG = Math.PI / 180;

type Vec3 = { e: number; n: number; u: number };

function len3(v: Vec3): number {
  return Math.hypot(v.e, v.n, v.u);
}

function normalize(v: Vec3): Vec3 {
  const l = len3(v);
  if (l < 1e-12) return { e: 0, n: 0, u: -1 };
  return { e: v.e / l, n: v.n / l, u: v.u / l };
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    e: a.n * b.u - a.u * b.n,
    n: a.u * b.e - a.e * b.u,
    u: a.e * b.n - a.n * b.e,
  };
}

function addScaled(
  base: Vec3,
  r: Vec3,
  u: Vec3,
  nx: number,
  ny: number,
  tanHalfH: number,
  tanHalfV: number,
): Vec3 {
  return normalize({
    e: base.e + nx * tanHalfH * r.e + ny * tanHalfV * u.e,
    n: base.n + nx * tanHalfH * r.n + ny * tanHalfV * u.n,
    u: base.u + nx * tanHalfH * r.u + ny * tanHalfV * u.u,
  });
}

/**
 * Cast a ray from the 3D map camera through a screen point; intersect the local
 * ground plane at the map center elevation.
 */
export function screenToLatLngOnMap3d(
  mapEl: Map3dElement,
  clientX: number,
  clientY: number,
): { lat: number; lng: number } | null {
  const center = readMapCenter(mapEl);
  if (!center) return null;

  const range = readMapRange(mapEl);
  const tilt = readMapTilt(mapEl) * DEG;
  const heading = readMapHeading(mapEl) * DEG;
  const fovV = readMapFov(mapEl) * DEG;

  const rect = mapEl.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const nx = ((clientX - rect.left) / rect.width - 0.5) * 2;
  const ny = ((clientY - rect.top) / rect.height - 0.5) * 2;

  const aspect = rect.width / rect.height;
  const tanHalfV = Math.tan(fovV / 2);
  const tanHalfH = tanHalfV * aspect;

  const sinT = Math.sin(tilt);
  const cosT = Math.cos(tilt);
  const sinH = Math.sin(heading);
  const cosH = Math.cos(heading);

  // Camera sits `range` from target; view axis pitched `tilt` from nadir toward `heading`.
  const camE = -range * sinH * sinT;
  const camN = -range * cosH * sinT;
  const camU = range * cosT;
  const C: Vec3 = { e: camE, n: camN, u: camU };

  const f = normalize({ e: -camE, n: -camN, u: -camU });
  const worldUp: Vec3 = { e: 0, n: 0, u: 1 };

  let r = cross(f, worldUp);
  if (len3(r) < 1e-6) r = { e: 1, n: 0, u: 0 };
  r = normalize(r);
  const uvec = normalize(cross(r, f));

  // Screen Y grows downward; negate so cursor-up moves the pick north on the ground.
  const rayDir = addScaled(f, r, uvec, nx, -ny, tanHalfH, tanHalfV);

  if (Math.abs(rayDir.u) < 1e-9) return null;
  const t = -C.u / rayDir.u;
  if (!(t > 0)) return null;

  const pe = C.e + t * rayDir.e;
  const pn = C.n + t * rayDir.n;

  const metersPerDegLat = 111_320;
  const metersPerDegLng =
    111_320 * Math.cos(center.lat * DEG) || 1e-6;

  return {
    lat: center.lat + pn / metersPerDegLat,
    lng: center.lng + pe / metersPerDegLng,
  };
}
